import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowRight, Globe } from "lucide-react";
import { NAV_LINKS, DOCTOR } from "../data/content";
import { useLang, LANGS } from "../i18n/i18n";

function LangSwitcher({ variant = "desktop" }) {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="lang-switcher"
      className={`flex items-center gap-1 border border-white/15 p-1 ${
        variant === "mobile" ? "self-start" : ""
      }`}
    >
      {variant === "desktop" && (
        <Globe className="mx-1.5 h-3.5 w-3.5 text-white/50" />
      )}
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`px-2.5 py-1 text-xs font-medium transition-colors duration-300 ${
            lang === l.code
              ? "bg-crimson text-white"
              : "text-white/60 hover:text-white"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-ink/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div
          data-testid="top-credential-bar"
          className={`w-full border-b transition-colors duration-500 ${
            scrolled
              ? "border-white/10 bg-ink/70 backdrop-blur-xl"
              : "border-crimson/25 bg-ink/40 backdrop-blur-md"
          }`}
        >
          <div className="mx-auto max-w-[1500px] px-6 py-2 md:px-12">
            <p className="text-center font-mono text-[9px] uppercase tracking-[0.15em] text-white/70 sm:text-[11px] md:tracking-[0.2em]">
              <span className="text-white">Dr. Devratsinh Parmar</span>
              <span className="text-crimson"> · MBBS · MD · DM</span>
              <span className="hidden md:inline text-white/60">
                {" "}· {t.topbarRole} · U. N. Mehta Institute of Cardiology, Ahmedabad
              </span>
              <span className="md:hidden text-white/60"> · {t.topbarRole}</span>
            </p>
          </div>
        </div>
        <nav className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-4 md:px-12">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center border border-crimson/60 text-crimson">
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                <path d="M12 21s-7.5-4.9-10-9.6C.3 8 1.5 4.5 4.8 4c2-.3 3.7.8 4.7 2.3C10.5 4.8 12.2 3.7 14.2 4c3.3.5 4.5 4 2.8 7.4C19.5 16.1 12 21 12 21z" />
              </svg>
            </span>
            <span className="font-serif text-lg leading-none tracking-wide text-white">
              Dr. Devratsinh
              <span className="block text-[10px] font-sans uppercase tracking-[0.28em] text-crimson">
                Parmar · Cardiology
              </span>
            </span>
          </Link>

          <div className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.key}`}
                className={({ isActive }) =>
                  `group relative text-sm uppercase tracking-[0.18em] transition-colors duration-300 ${
                    isActive ? "text-crimson" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative inline-block py-1">
                    {t.nav[l.key]}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-[2px] bg-crimson transition-all duration-300 ease-out ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden lg:block">
              <LangSwitcher />
            </div>
            <a
              href={DOCTOR.phoneHref}
              data-testid="nav-call-button"
              className="group hidden items-center gap-2 border border-white/15 px-5 py-2.5 text-xs uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-crimson hover:bg-crimson md:flex"
            >
              <Phone className="h-3.5 w-3.5 text-crimson transition-colors group-hover:text-white" />
              {t.nav.consult}
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((o) => !o)}
              className="text-white lg:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/97 backdrop-blur-xl px-8 lg:hidden"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.div
                key={l.path}
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
              >
                <NavLink
                  to={l.path}
                  data-testid={`mobile-nav-link-${l.key}`}
                  className={({ isActive }) =>
                    `group flex items-center border-b border-white/10 py-4 font-serif text-3xl transition-colors duration-300 ${
                      isActive ? "text-crimson" : "text-white hover:text-crimson"
                    }`
                  }
                >
                  <span className="mr-4 font-mono text-xs text-crimson transition-transform duration-300 group-hover:translate-x-1">
                    0{i + 1}
                  </span>
                  <span className="relative inline-block">
                    {t.nav[l.key]}
                    <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-crimson transition-all duration-300 ease-out group-hover:w-full" />
                  </span>
                  <ArrowRight className="ml-auto h-6 w-6 -translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                </NavLink>
              </motion.div>
            ))}
            <div className="mt-8">
              <LangSwitcher variant="mobile" />
            </div>
            <a
              href={DOCTOR.phoneHref}
              className="mt-6 flex items-center gap-2 text-crimson"
            >
              <Phone className="h-4 w-4" /> {DOCTOR.phone}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
